package com.eladfinish.bible.contest.utils

import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.constants.StringConstants.MAKAF
import com.eladfinish.bible.contest.constants.StringConstants.MYSTERY_CHAR_ZWJ
import com.eladfinish.bible.contest.constants.StringConstants.NEW_LINE
import com.eladfinish.bible.contest.constants.StringConstants.NO_BREAK_SPACE
import com.eladfinish.bible.contest.constants.StringConstants.NUN_MENUZERET
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OTIOT_1_TO_3
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OTIOT_2_PLUS
import com.eladfinish.bible.contest.constants.StringConstants.Regex.WORD_CHAR
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.extensions.runIfNot
import com.eladfinish.bible.contest.utils.MyStringUtils.replaceSpaceWithNoBreakSpace

/**
 * Developed by
 * @author Elad Finish
 */
//fun String.removeWhitespacesAndParshiot(): String = this
//    .replaceRegex("\\{.+?\\}", "") //.replaceAll("(\\r|\\n)", " ")
//    .replaceRegex("([\\r\\n])", " ")
//    .replaceRegex(NEW_LINE, " ")
//    .replaceRegex("\\s+", " ").trim()
//    .replaceRegex(MYSTERY_CHAR_ZWJ.toString(), "")
//    .replaceRegex("$MAKAF ", MAKAF.toString())
//    .trim()

// this method catch the first ot pasuk (since it's not prefixed by a colonSplitter)
// WORD_CHAR lookarounds rather than \b: JS RegExp's \b is ASCII-only (see WORD_CHAR).
fun getRegexOfSingleOtPasukAleph(): String =
    "(?<!$WORD_CHAR)(א)(?!$WORD_CHAR)"

// this method catch the first ot pasuk (since it's not prefixed by a colonSplitter)
fun getRegexOfSingleOtPasuk(): String =
    "(?<!$WORD_CHAR)$OTIOT_1_TO_3(?!$WORD_CHAR)"

// this method doesn't catch the first ot pasuk (since it's not prefixed by a colonSplitter)
// NOTE: the ot-pasuk is followed by a NO-BREAK SPACE in displayed content (see
// replaceSpaceWithNoBreakSpace). `\s` matches NBSP on Android's ICU regex but NOT on
// Kotlin/Native (iOS) or OpenJDK, so the trailing whitespace class must include NBSP
// explicitly — otherwise the ot-pasuk letters are not matched on iOS (e.g. not shrunk).
// The `פרק(?:<ws><letters>)?` alternative also catches the FIRST verse letter of a section that
// starts mid-chapter (e.g. a haftarah: "פרק מב ה ..." — ה is preceded by the chapter number,
// not by a colonSplitter).
// On iOS this regex is replaced by an equivalent scanner — see OtiotPasukPlatform.kt; keep the
// two in sync (OtiotPasukPlatformTest asserts their equivalence).
//
// No \b around the letters: the whitespace on both sides already implies it. JS RegExp's
// ASCII-only \b would never match next to a Hebrew letter (so nothing matched on web), and on
// desktop (plain OpenJDK java.util.regex) \b never fires between a Hebrew letter and whitespace
// either, so no ot-pasuk letter was shrunk there.
fun getRegexOfOtiotPasuk(): String =
    "(?<=(?:\\]|פרק(?:[\\s$NO_BREAK_SPACE]$ALEF_TAV{1,3})?|$COLON_SPLITTER)[\\s$NO_BREAK_SPACE]{1,2})($ALEF_TAV{1,3})([\\s$NO_BREAK_SPACE])"

// `\b` is deliberately NOT used in `(?!פרק\b)`: on desktop (plain OpenJDK java.util.regex),
// `\b` never fires between a Hebrew letter and plain whitespace/string-end — see
// getRegexOfOtiotPasuk() above for the full explanation — so `פרק\b` never matched there, making
// the negative lookahead trivially succeed and letting the literal word "פרק" get wrongly removed
// whenever it followed a nun menuzeret. Replaced with an explicit "not followed by another word
// character" lookahead (see WORD_CHAR), which is what \b actually needs to mean here (it must
// still tell "פרק" the standalone word apart from the start of a longer word like "פרקים") and
// works identically on every platform, web included.
fun getRegexOfOtiotPasukAfterNunMenuzeret(): String =
    "(?<=$NUN_MENUZERET\\s)(?!פרק(?!$WORD_CHAR))$ALEF_TAV{1,3}"

// avoid line starting with pasek
fun String.setNoBreakSpaceBeforePasek(): String =
    this.replaceRegex(' '.toString() + "(׀)", "$NO_BREAK_SPACE$1")

fun String.addGershaimAndColon(): String =
    "\"$this$COLON_SPLITTER\""

fun String.wrapByGershaim(): String =
    "\"$this\""

fun String.removeWhitespacesAndParshiot(
    isTikunKorim: Boolean = false,
): String =
    this
        .runIf(condition = isTikunKorim) {
            replaceRegex(
                regex = "\\{פ\\}",
                replacement = "\n"
            ).replaceRegex(
                regex = "\\{ש\\}\\s?",
                replacement = "\n"
            ).replaceRegex(
                regex = "\\{ס\\}",
                replacement = "         " // 9 spaces
//                replacement = "\t\t\t\t\t\t"
            ).replaceRegex(
                regex = "\\{ר\\}",
                replacement = "     " // 5 spaces
            )
        }
        .runIfNot(condition = isTikunKorim) {
            replaceRegex(
                regex = "\\{.+?\\}",
                replacement = ""
            ).replaceRegex(
                regex = "[\\r\\n]",
                replacement = " "
            ).replaceRegex(
                regex = NEW_LINE,
                replacement = " "
            ).replaceRegex(
                regex = "\\s+",
                replacement = " "
            )
        }
        .trim()
        .replaceRegex(
            regex = "$MYSTERY_CHAR_ZWJ",
            replacement = ""
        )
        .replaceRegex(
            regex = "$MAKAF ",
            replacement = MAKAF.toString()
        )
        .trim()


// TODO: rename to replaceKtivWithQriNewV2
fun String.replaceKtivWithQriNew(isTikunKorim: Boolean = false): String {
    val leftBracket = "\\("
    val rightBracket = "\\)" //as characters
    val noBrackets = "[^()]+"
    val qriExpression =  // for capturing group
        "$leftBracket($noBrackets)$rightBracket"
    return this
        .runIfNot(condition = isTikunKorim) {
            replaceRegex(
                regex = "$NUN_MENUZERET\\s?",
                replacement = ""
            )
        }
        //negative look-behind: matches " word word (qri)" that is not preceded by a colonSplitter
        .replaceRegex(
            regex = "(?<![$COLON_SPLITTER}])\\s$OTIOT_2_PLUS\\s$OTIOT_2_PLUS$MAKAF?\\s$qriExpression",
            replacement = " " + "$1"
        ) //                        + "\\s" + alef_tav + "\\s" + qri_expression, " " + "$1")
        //                .replaceAll("([^"+colonSplitter+"])"+"\\s" + alef_tav + "\\s" + alef_tav + "\\s" + qri_expression,"$1" + " " + "$2")
        //below line to replace a ktiv of two words with a makaf between them
        //with a qri of two words with a makaf between them
        .replaceRegex(
            regex = "(?<!$WORD_CHAR)$OTIOT_2_PLUS$MAKAF$OTIOT_2_PLUS\\s$qriExpression",
            replacement = "$1"
        )
        .replaceRegex(
            regex = "$MAKAF$OTIOT_2_PLUS\\s$qriExpression",
            replacement = "$MAKAF$1"
        )
        //                .replaceAll(alef_tav + "\\s" + qri_expression, "$1")
        //                .replaceAll(alef_tav + MAKAF + "\\s" + qri_expression, "$1")
        .replaceRegex(
            regex = "$OTIOT_2_PLUS$MAKAF?\\s$qriExpression",
            replacement = "$1"
        )
        .replaceRegex(
            regex = qriExpression,
            replacement = "$1"
        ) //qri and not ktiv
        .replaceRegex(
            regex = "$MAKAF ",
            replacement = MAKAF.toString()
        )
        .replaceRegex(
            regex = "\\s+",
            replacement = " "
        )
}

fun String.beautifyText(): String =
    this
        .replaceKtivWithQriNew()
        .removeWhitespacesAndParshiot()
        .replaceSpaceWithNoBreakSpace()
